import { useMemo } from "react";
import { ApiKeysEntity } from "entities/ApiKeys/ApiKeysEntity";
import useRolesPermissions from "./useRolesPermissions";

/**
 * Enable-gate for the CBO API Key Management page (OS-2550, AC-1), mirroring
 * useAllowCorporateDashboard. The page/nav is shown only when BOTH:
 *   - the caller has the `showAPIKeyManagement` sidebar permission (ADMINISTRATOR), AND
 *   - the Corporation's `third_party_api_corporate_config.is_enabled` is true.
 * A missing config row reads as disabled (fail-closed). The enable-gate query is skipped for
 * users without the permission so non-admins never hit a denied Hasura read.
 *
 * Also threads the Corporation's DB-driven `max_active_api_keys` cap (OS-2552, D-2552-3) to
 * consumers — same config read, no extra query.
 */
export const useAllowApiKeyManagement = () => {
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const showApiKeyManagement = Boolean(CORPORATE_PERMISSIONS?.sidebarPermissions?.showAPIKeyManagement);

    const { isEnabled, loading, maxActiveApiKeys } = ApiKeysEntity.GetThirdPartyApiCorporateConfig(
        !showApiKeyManagement,
    );

    const isAllowedToUseApiKeyManagement = useMemo(
        () => showApiKeyManagement && isEnabled,
        [showApiKeyManagement, isEnabled],
    );

    // While the gate read is in flight the answer is unknown — consumers should show a loading
    // state, not the "not enabled for your organization" message (it would flash for allowed admins).
    return { isAllowedToUseApiKeyManagement, isAllowLoading: showApiKeyManagement && loading, maxActiveApiKeys };
};
